import type { CheckboxProps } from "antd";
import { Card, Image, List, Modal } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustomButton, ProductSpec } from "../../../../components";
import { useShoppingCart } from "../../../../context/ShoppingCartContext";
import localStorageKey from "../../../../enumerations/localStorageKey";
import { useGetProductItem } from "../../../../hooks/ShopCart/cart.hooks";
import { pushGtag } from "../../../../plugins/gtagFunction";
import { moneyFormat } from "../../../../plugins/numberFormat";
import AddIcon from "../../icons/AddIcon";
import MinusIcon from "../../icons/MinusIcon";
import RemoveIcon from "../../icons/RemoveIcon";
import "../../style.scss";
import styles from "./style.module.scss";

const ShopCart = ({ next }: { next: (selectedIds: number[]) => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [productIds, setProductIds] = useState<any>();
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [subtotal, setSubtotal] = useState<number>(0);
  const [filteredItems, setFilteredItems] = useState<any>();
  const [totalPrice, setTotalPrice] = useState<number>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [itemToRemove, setItemToRemove] = useState<number | null>(null);
  const storeCode = window.localStorage.getItem(localStorageKey.STORE_CODE);
  const nowLang = window.localStorage.getItem(localStorageKey.DEFAULT_LANG);
  const { updateCart } = useShoppingCart();
  const { data: products } = useGetProductItem(
    { code: storeCode, item_id: productIds },
    { enabled: !!productIds && !!((productIds?.length ?? 0) > 0) }
  );

  //checkbox
  const handleCheckboxChange = (selected: number[]) => {
    setSelectedItems(selected);
    const isIndeterminate =
      selected.length > 0 && selected.length < filteredItems?.length;

    setSelectAll(
      isIndeterminate ? false : selected.length === filteredItems?.length
    );
  };

  const handleSelectAllChange: CheckboxProps["onChange"] = (e: any) => {
    setSelectAll(e.target.checked);
    const allItemIds = filteredItems?.map((item: any) => item.id);
    setSelectedItems(e.target.checked ? allItemIds : []);
  };

  //Qty
  const initializeQuantities = (data: any) => {
    const initialQuantities: { [key: number]: number } = {};

    data?.forEach((item: any) => {
      initialQuantities[item.id] = item.quantity;
    });
    setQuantities(initialQuantities);
  };

  const handleMinusAmount = (itemId: number) => {
    if (quantities[itemId] > 1) {
      const updatedQuantities = {
        ...quantities,
        [itemId]: quantities[itemId] - 1,
      };
      setQuantities(updatedQuantities);
      updateCart(itemId, -1);
      const item = getItemById(itemId);
      setSubtotal((prevSubtotal) => prevSubtotal - item.store_price);
    }

    if (quantities[itemId] <= 1) {
      setItemToRemove(itemId);
      setOpenModal(true);
    }
  };

  const handleAddAmount = (itemId: number) => {
    const updatedQuantities = {
      ...quantities,
      [itemId]: quantities[itemId] + 1,
    };
    setQuantities(updatedQuantities);

    updateCart(itemId, +1);
    const item = getItemById(itemId);
    setSubtotal((prevSubtotal) => prevSubtotal - item.store_price);
  };

  const getItemById = (itemId: number) => {
    return products.find((item: any) => item.id === itemId)!;
  };

  //remove
  const handleRemoveItem = (itemId: number) => {
    const removedItem = products.find((item: any) => item.id === itemId);

    if (removedItem) {
      setFilteredItems((prevFilteredItems: any) =>
        prevFilteredItems.filter((item: any) => item.id !== itemId)
      );

      setSelectedItems((prevSelectedItems) =>
        prevSelectedItems.filter((id) => id !== itemId)
      );

      setSubtotal(
        (prevSubtotal) =>
          prevSubtotal - quantities[itemId] * removedItem.store_price
      );

      setQuantities((prevQuantities) => {
        const updatedQuantities = { ...prevQuantities };
        delete updatedQuantities[itemId];
        return updatedQuantities;
      });

      updateCart(itemId, -quantities[itemId]);
    }
  };

  const handleSubmit = () => {
    pushGtag({
      event: "cart_checkout",
    });

    if (window.localStorage.getItem(localStorageKey.USER_ACCOUNT)) {
      next(selectedItems);
    } else {
      navigate("/signin");
    }
  };

  const handleModalConfirm = () => {
    if (itemToRemove) {
      handleRemoveItem(itemToRemove);
      setOpenModal(false);
    }
  };

  useEffect(() => {
    const _local: any = window.localStorage.getItem(localStorageKey.CART);
    const renderData = JSON.parse(_local);
    setProductIds(renderData?.map((i: any) => i.id));
    initializeQuantities(renderData);
  }, []);

  useEffect(() => {
    setFilteredItems(products);
  }, [products]);

  useEffect(() => {
    let total = 0;
    filteredItems?.forEach((item: any) => {
      if (selectedItems?.includes(item.id)) {
        total += quantities[item.id] * item.store_price;
      }
      if (quantities[item.id] < 1) {
        setOpenModal(true);
      }
    });
    setTotalPrice(total);
  }, [quantities, filteredItems, selectedItems]);

  useEffect(() => {
    if (filteredItems) {
      const allItemIds = filteredItems?.map((item: any) => item.id);
      setSelectedItems(allItemIds);
    }
  }, [filteredItems]);

  const renderItem = (item: any) => (
    <List.Item key={item?.id} className={styles.cardItems}>
      <div className={styles.card}>
        <div className={styles.content}>
          <div className={styles.productName}>
            <div style={{ marginRight: 8 }}>
              {/* <Checkbox
            value={item.id}
            checked={selectedItems.includes(item.id)}
            onChange={(e) =>
              handleCheckboxChange(
                e.target.checked
                  ? [...selectedItems, item.id]
                  : selectedItems.filter((id) => id !== item.id)
              )
            }
          >
            <Image src={item?.img} style={{ width: "48px" }} preview={false} />
          </Checkbox> */}
              <Image
                src={item?.img}
                style={{ width: "48px" }}
                preview={false}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div className={styles.contentMain}>
                {nowLang === "en-US" ? item?.name_en_US : item?.name_zh_TW}
              </div>
              <div className={styles.contentDesc}>
                {ProductSpec(t, item.network_volume, item.type, item.days)}
              </div>
              {item?.hidden_store_code ? (
                <span className={styles.contentPromoteHiddenStore}>
                  {t("normal.promotion.hiddenStore")}
                </span>
              ) : (
                <></>
              )}
            </div>
          </div>
          <div className={styles.unitPrice}>{`NT $${moneyFormat(
            item?.store_price
          )}`}</div>
          <div className={styles.qtyContainer}>
            <div className={styles.icons}>
              <MinusIcon onClick={() => handleMinusAmount(item.id)} />
            </div>
            <div className={styles.qtyBox}>{quantities[item.id]}</div>
            <div className={styles.icons}>
              <AddIcon onClick={() => handleAddAmount(item.id)} />
            </div>
          </div>
          <div className={styles.unitPrice}>{`NT $${moneyFormat(
            quantities[item.id] * item?.store_price
          )}`}</div>
          <div className={styles.removeIcon}>
            <RemoveIcon
              onClick={() => {
                setOpenModal(true);
                setItemToRemove(item.id);
              }}
            />
          </div>
        </div>
      </div>
    </List.Item>
  );

  return (
    <div className={`${styles.mainContainer} Cart-container`}>
      <div className={styles.cartContainter}>
        <Card
          // title={
          //   <>
          //     <Checkbox
          //       checked={selectAll}
          //       onChange={handleSelectAllChange}
          //       disabled={!filteredItems || filteredItems?.length <= 0}
          //       indeterminate={
          //         selectedItems.length > 0 &&
          //         selectedItems.length < filteredItems?.length
          //       }
          //     >
          //       {t("normal.cart")}
          //     </Checkbox>
          //   </>
          // }
          title={t("normal.cart")}
        >
          <div className={styles.listSubHeader}>
            <div style={{ width: "40%" }}>{t("cart.productName")}</div>
            <div style={{ width: "10%", textAlign: "center" }}>
              {t("cart.unitPrice")}
            </div>
            <div style={{ width: "30%", textAlign: "center" }}>
              {t("normal.quantity")}
            </div>
            <div style={{ width: "10%", textAlign: "center" }}>
              {t("cart.subtotal")}
            </div>
            <div style={{ width: "10%", textAlign: "center" }}>
              {t("normal.remove")}
            </div>
          </div>
          <List
            dataSource={filteredItems}
            renderItem={renderItem}
            footer={
              <div className={styles.listFooter}>
                <span>{t("cart.total")}</span>
                <div className={styles.totalPrice}>{`NT $${moneyFormat(
                  totalPrice
                )}`}</div>
              </div>
            }
          />
        </Card>
        <div className={styles.btnGroup}>
          <CustomButton
            type="primary"
            danger
            htmlType="submit"
            disabled={selectedItems.length ? false : true}
            onClick={handleSubmit}
            style={{ width: "100%", maxWidth: "343px" }}
          >
            {t("cart.checkout")}
          </CustomButton>
        </div>
      </div>
      <Modal
        open={openModal}
        onCancel={() => setOpenModal(false)}
        onOk={handleModalConfirm}
        destroyOnClose={true}
        centered
      >
        {t("cart.removeItem")}
      </Modal>
    </div>
  );
};

export default ShopCart;
